import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { getAdminToken, setAdminToken, clearAdminToken } from "@/lib/auth";
import { toast } from "sonner";
import { Loader2, Lock, LogOut, Plus, Trash2, Edit3, Newspaper, FileDown, Inbox, Users, Package, Sparkles, Bell, Send } from "lucide-react";

const authHeader = () => ({ headers: { Authorization: `Bearer ${getAdminToken()}` } });

export default function Admin() {
  const nav = useNavigate();
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const token = getAdminToken();
    if (!token) { setChecking(false); return; }
    api.get("/admin/summary", authHeader())
      .then(() => setUser({ token }))
      .catch(() => clearAdminToken())
      .finally(() => setChecking(false));
  }, []);

  const handleLogin = useCallback((t) => {
    setAdminToken(t);
    setUser({ token: t });
  }, []);

  const handleLogout = useCallback(() => {
    clearAdminToken();
    setUser(null);
    nav("/admin");
  }, [nav]);

  if (checking) return <div className="min-h-[70vh] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-gold-500" /></div>;
  if (!user) return <AdminLogin onLoggedIn={handleLogin} />;
  return <AdminDashboard onLogout={handleLogout} />;
}

function AdminLogin({ onLoggedIn }) {
  const [form, setForm] = useState({ email: "admin@sbcltd.com", password: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { data } = await api.post("/admin/login", form);
      toast.success("Welcome");
      onLoggedIn(data.token);
    } catch (err) { toast.error(err.response?.data?.detail || "Login failed"); }
    setLoading(false);
  };
  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-24 bg-muted/30" data-testid="admin-login">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="w-12 h-12 mx-auto rounded-full bg-neutral-950 dark:bg-white flex items-center justify-center">
            <Lock className="w-5 h-5 text-gold-400" />
          </div>
          <div className="eyebrow mt-4">Admin CMS</div>
          <h1 className="font-display font-bold text-2xl mt-2">SBC Content Management</h1>
        </div>
        <form onSubmit={submit} className="bg-background rounded-2xl border border-border p-6 space-y-3" data-testid="admin-login-form">
          <F label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testId="admin-email" />
          <F label="Password" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} required testId="admin-password" />
          <button disabled={loading} className="w-full bg-neutral-950 dark:bg-white dark:text-neutral-950 text-white rounded-full h-11 font-semibold flex items-center justify-center gap-2 disabled:opacity-50" data-testid="admin-login-submit">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />} Sign In
          </button>
        </form>
      </div>
    </div>
  );
}

function AdminDashboard({ onLogout }) {
  const [tab, setTab] = useState("summary");
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    api.get("/admin/summary", authHeader()).then((r) => setSummary(r.data)).catch(() => onLogout());
  }, [onLogout]);

  return (
    <div className="min-h-[80vh] bg-muted/30" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex justify-between items-start mb-8">
          <div>
            <div className="eyebrow">Admin CMS</div>
            <h1 className="font-display font-bold text-4xl tracking-tighter mt-1">SBC Console</h1>
          </div>
          <button onClick={onLogout} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-gold-500" data-testid="admin-logout"><LogOut className="w-4 h-4" /> Logout</button>
        </div>

        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-8">
            {Object.entries(summary).map(([k, v]) => (
              <div key={k} className="p-4 rounded-xl bg-background border border-border" data-testid={`admin-stat-${k}`}>
                <div className="font-display font-black text-2xl gold-gradient-text">{v}</div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">{k.replace(/_/g, " ")}</div>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-2 border-b border-border overflow-x-auto no-scrollbar mb-6">
          {[
            { k: "summary", label: "Overview", Icon: Users },
            { k: "variants", label: "Brand Variants", Icon: Package },
            { k: "news", label: "News", Icon: Newspaper },
            { k: "downloads", label: "Downloads", Icon: FileDown },
            { k: "push", label: "Push Notifications", Icon: Bell },
            { k: "submissions", label: "Submissions", Icon: Inbox },
          ].map(({ k, label, Icon }) => (
            <button key={k} onClick={() => setTab(k)} className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap ${tab === k ? "border-gold-400 text-gold-500" : "border-transparent hover:text-gold-500"}`} data-testid={`admin-tab-${k}`}>
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>

        {tab === "summary" && <SummaryTab />}
        {tab === "variants" && <VariantsTab />}
        {tab === "news" && <NewsTab />}
        {tab === "downloads" && <DownloadsTab />}
        {tab === "push" && <PushTab />}
        {tab === "submissions" && <SubmissionsTab />}
      </div>
    </div>
  );
}

function SummaryTab() {
  return (
    <div className="bg-background rounded-2xl border border-border p-8">
      <h2 className="font-display font-bold text-xl">Quick actions</h2>
      <p className="text-muted-foreground text-sm mt-2">Use the tabs above to manage News, Downloads and review incoming Submissions.</p>
      <ul className="mt-6 grid sm:grid-cols-2 gap-3 text-sm">
        <li className="p-4 rounded-lg border border-border">News: Publish stories that appear on the public /news page.</li>
        <li className="p-4 rounded-lg border border-border">Downloads: Upload/list PDFs shown on /downloads.</li>
        <li className="p-4 rounded-lg border border-border">Submissions: View contact, distributor, career, investor and sample requests.</li>
        <li className="p-4 rounded-lg border border-border">Security: Change your admin password by contacting the site engineer.</li>
      </ul>
    </div>
  );
}

function NewsTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const empty = { title: "", category: "Corporate News", date: new Date().toISOString().slice(0, 10), excerpt: "", body: "", image: "", published: true };
  const load = useCallback(() => api.get("/news").then((r) => setItems(r.data.items || [])), []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    try {
      if (editing.id) await api.put(`/admin/news/${editing.id}`, editing, authHeader());
      else await api.post("/admin/news", editing, authHeader());
      toast.success("Saved"); setEditing(null); load();
    } catch { toast.error("Save failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this news item?")) return;
    await api.delete(`/admin/news/${id}`, authHeader());
    load();
  };
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="font-display font-bold text-xl">News Articles</h2>
        <button onClick={() => setEditing(empty)} className="inline-flex items-center gap-2 bg-neutral-950 dark:bg-white dark:text-neutral-950 text-white rounded-full px-4 py-2 text-sm font-semibold" data-testid="admin-news-add"><Plus className="w-4 h-4" /> Add</button>
      </div>
      <div className="rounded-2xl border border-border divide-y divide-border bg-background">
        {items.map((n) => (
          <div key={n.id} className="p-4 flex items-center justify-between gap-4" data-testid={`admin-news-row-${n.id}`}>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-gold-500 font-bold">{n.category} · {n.date}</div>
              <div className="font-semibold truncate">{n.title}</div>
              <div className="text-xs text-muted-foreground truncate">{n.excerpt}</div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setEditing(n)} className="w-8 h-8 rounded-md border border-border hover:border-gold-400 flex items-center justify-center"><Edit3 className="w-3.5 h-3.5" /></button>
              <button onClick={() => remove(n.id)} className="w-8 h-8 rounded-md border border-border hover:border-red-500 text-red-500 flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        ))}
      </div>
      {editing && <EditorModal title={editing.id ? "Edit article" : "New article"} onClose={() => setEditing(null)} onSave={save}>
        <F label="Title" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} />
        <F label="Category" value={editing.category} onChange={(v) => setEditing({ ...editing, category: v })} />
        <F label="Date" type="date" value={editing.date} onChange={(v) => setEditing({ ...editing, date: v })} />
        <F label="Image URL" value={editing.image} onChange={(v) => setEditing({ ...editing, image: v })} />
        <TA label="Excerpt" value={editing.excerpt} onChange={(v) => setEditing({ ...editing, excerpt: v })} rows={2} />
        <TA label="Body" value={editing.body} onChange={(v) => setEditing({ ...editing, body: v })} rows={5} />
      </EditorModal>}
    </div>
  );
}

function DownloadsTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const empty = { title: "", category: "Corporate", size: "", type: "PDF", url: "#" };
  const load = useCallback(() => api.get("/downloads").then((r) => setItems(r.data.items || [])), []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    if (editing.id) await api.put(`/admin/downloads/${editing.id}`, editing, authHeader());
    else await api.post("/admin/downloads", editing, authHeader());
    toast.success("Saved"); setEditing(null); load();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this download?")) return;
    await api.delete(`/admin/downloads/${id}`, authHeader());
    load();
  };
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="font-display font-bold text-xl">Downloads</h2>
        <button onClick={() => setEditing(empty)} className="inline-flex items-center gap-2 bg-neutral-950 dark:bg-white dark:text-neutral-950 text-white rounded-full px-4 py-2 text-sm font-semibold" data-testid="admin-download-add"><Plus className="w-4 h-4" /> Add</button>
      </div>
      <div className="rounded-2xl border border-border divide-y divide-border bg-background">
        {items.map((d) => (
          <div key={d.id} className="p-4 flex items-center justify-between gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-gold-500 font-bold">{d.category} · {d.type} · {d.size}</div>
              <div className="font-semibold">{d.title}</div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setEditing(d)} className="w-8 h-8 rounded-md border border-border hover:border-gold-400 flex items-center justify-center"><Edit3 className="w-3.5 h-3.5" /></button>
              <button onClick={() => remove(d.id)} className="w-8 h-8 rounded-md border border-border hover:border-red-500 text-red-500 flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        ))}
      </div>
      {editing && <EditorModal title={editing.id ? "Edit download" : "New download"} onClose={() => setEditing(null)} onSave={save}>
        <F label="Title" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} />
        <F label="Category" value={editing.category} onChange={(v) => setEditing({ ...editing, category: v })} />
        <div className="grid grid-cols-2 gap-3">
          <F label="Size" value={editing.size} onChange={(v) => setEditing({ ...editing, size: v })} />
          <F label="Type" value={editing.type} onChange={(v) => setEditing({ ...editing, type: v })} />
        </div>
        <F label="URL" value={editing.url} onChange={(v) => setEditing({ ...editing, url: v })} />
      </EditorModal>}
    </div>
  );
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const absoluteImage = (url) => (!url ? null : url.startsWith("http") ? url : `${BACKEND_URL}${url}`);

function VariantsTab() {
  const [items, setItems] = useState([]);
  const [brandFilter, setBrandFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [generating, setGenerating] = useState(null);
  const empty = { brand_slug: "onecola", name: "", note: "", accent: "#D4AF37", packaging: "aluminium can", label_description: "", sizes: [], sort_order: 100, published: true };
  const brands = ["onecola", "maltora", "onefresh", "voltx", "onespring"];
  const load = useCallback(() => api.get("/variants/all").then((r) => setItems(r.data.items || [])), []);
  useEffect(() => { load(); }, [load]);

  const filtered = items.filter((v) => brandFilter === "all" || v.brand_slug === brandFilter);

  const save = async () => {
    if (!editing.name) { toast.error("Name required"); return; }
    if (editing.id) await api.put(`/admin/variants/${editing.id}`, editing, authHeader());
    else await api.post("/admin/variants", editing, authHeader());
    toast.success("Saved"); setEditing(null); load();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this variant?")) return;
    await api.delete(`/admin/variants/${id}`, authHeader());
    load();
  };
  const generate = async (v) => {
    setGenerating(v.id);
    const brandDisplay = { onecola: "OneCola", maltora: "Maltora", onefresh: "OneFresh", voltx: "VoltX", onespring: "OneSpring" }[v.brand_slug] || v.brand_slug;
    try {
      const { data } = await api.post("/admin/variants/generate-image", {
        variant_id: v.id, brand: brandDisplay, variant_name: v.name,
        packaging: v.packaging || "aluminium can", label_description: v.label_description || "", force: true,
      }, authHeader());
      toast.success("Image generated");
      // update in place
      setItems((its) => its.map((x) => x.id === v.id ? { ...x, image_url: data.image_url } : x));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Generation failed");
    }
    setGenerating(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h2 className="font-display font-bold text-xl">Brand Variants</h2>
        <div className="flex gap-2 items-center flex-wrap">
          <select value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)} className="h-9 rounded-full border border-border bg-background px-3 text-xs" data-testid="variant-brand-filter">
            <option value="all">All brands</option>
            {brands.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
          <button onClick={() => setEditing(empty)} className="inline-flex items-center gap-2 bg-neutral-950 dark:bg-white dark:text-neutral-950 text-white rounded-full px-4 py-2 text-sm font-semibold" data-testid="admin-variant-add"><Plus className="w-4 h-4" /> New Variant</button>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {filtered.map((v) => (
          <div key={v.id} className="rounded-2xl bg-background border border-border overflow-hidden" data-testid={`admin-variant-${v.id}`}>
            <div className="aspect-square relative bg-muted flex items-center justify-center">
              {v.image_url ? (
                <img src={absoluteImage(v.image_url)} alt={v.name} className="w-full h-full object-cover" />
              ) : (
                <div className="text-xs text-muted-foreground">No image</div>
              )}
              <span className="absolute top-2 left-2 text-[9px] uppercase tracking-widest bg-black/60 text-white px-2 py-0.5 rounded-full">{v.brand_slug}</span>
            </div>
            <div className="p-4 space-y-2">
              <div className="font-semibold text-sm">{v.name}</div>
              <div className="text-xs text-muted-foreground line-clamp-2">{v.note || "—"}</div>
              <div className="text-[10px] text-muted-foreground">{v.sizes?.length || 0} sizes · {v.packaging}</div>
              <div className="flex gap-1 pt-2">
                <button onClick={() => generate(v)} disabled={generating === v.id} className="flex-1 inline-flex items-center justify-center gap-1 text-[11px] font-semibold text-gold-600 hover:bg-gold-400/10 rounded-md py-1.5 border border-border disabled:opacity-50" data-testid={`variant-generate-${v.id}`}>
                  {generating === v.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} AI Image
                </button>
                <button onClick={() => setEditing(v)} className="w-8 h-8 rounded-md border border-border hover:border-gold-400 flex items-center justify-center"><Edit3 className="w-3.5 h-3.5" /></button>
                <button onClick={() => remove(v.id)} className="w-8 h-8 rounded-md border border-border hover:border-red-500 text-red-500 flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {editing && <EditorModal title={editing.id ? "Edit variant" : "New variant"} onClose={() => setEditing(null)} onSave={save}>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground mb-1">Brand</div>
            <select value={editing.brand_slug} onChange={(e) => setEditing({ ...editing, brand_slug: e.target.value })} className="w-full h-10 rounded-lg border border-border bg-background px-3 text-sm">
              {brands.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <F label="Name" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} required testId="variant-name" />
        </div>
        <F label="Note / Tagline" value={editing.note} onChange={(v) => setEditing({ ...editing, note: v })} />
        <F label="Accent (hex)" value={editing.accent} onChange={(v) => setEditing({ ...editing, accent: v })} />
        <F label="Packaging (e.g. 'aluminium can', 'PET bottle')" value={editing.packaging} onChange={(v) => setEditing({ ...editing, packaging: v })} />
        <TA label="Label design description (used by AI image gen)" value={editing.label_description} onChange={(v) => setEditing({ ...editing, label_description: v })} rows={3} />
        <F label="Image URL (auto-set on AI generate)" value={editing.image_url} onChange={(v) => setEditing({ ...editing, image_url: v })} />
        <TA label="Sizes JSON (e.g. [{&quot;label&quot;:&quot;330ml&quot;,&quot;format&quot;:&quot;Can&quot;}])" value={JSON.stringify(editing.sizes || [], null, 2)} onChange={(v) => { try { setEditing({ ...editing, sizes: JSON.parse(v) }); } catch { /* ignore parse errors */ } }} rows={4} />
      </EditorModal>}
    </div>
  );
}

function SubmissionsTab() {
  const [kind, setKind] = useState("contact");
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get(`/admin/submissions?kind=${kind}`, authHeader()).then((r) => setItems(r.data.items || []));
  }, [kind]);
  const kinds = [
    { k: "contact", label: "Contact" }, { k: "distributor", label: "Distributor" },
    { k: "career", label: "Career" }, { k: "investor", label: "Investor" },
    { k: "sample", label: "Sample Requests" }, { k: "newsletter", label: "Newsletter" },
  ];
  return (
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap">
        {kinds.map((x) => (
          <button key={x.k} onClick={() => setKind(x.k)} className={`px-3 py-1.5 rounded-full text-xs font-medium border ${kind === x.k ? "bg-neutral-950 text-white border-neutral-950 dark:bg-white dark:text-neutral-950 dark:border-white" : "border-border hover:border-gold-400"}`} data-testid={`admin-sub-${x.k}`}>
            {x.label}
          </button>
        ))}
      </div>
      <div className="bg-background rounded-2xl border border-border overflow-hidden">
        {items.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">No submissions yet.</div>}
        {items.map((it) => (
          <details key={it.id || `${it.email || ""}-${it.created_at || ""}`} className="border-b border-border last:border-b-0">
            <summary className="p-4 cursor-pointer hover:bg-muted/50 flex justify-between text-sm">
              <span className="font-semibold">{it.email || it.name || it.full_name || it.company || "Submission"}</span>
              <span className="text-muted-foreground text-xs">{it.created_at?.slice(0, 19)}</span>
            </summary>
            <pre className="p-4 text-xs bg-muted/30 overflow-x-auto whitespace-pre-wrap">{JSON.stringify(it, null, 2)}</pre>
          </details>
        ))}
      </div>
    </div>
  );
}

function EditorModal({ title, children, onClose, onSave }) {
  return (
    <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-2xl bg-background rounded-2xl border border-border p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-display font-bold text-xl">{title}</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-muted flex items-center justify-center">×</button>
        </div>
        <div className="space-y-3">{children}</div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-full text-sm border border-border">Cancel</button>
          <button onClick={onSave} className="px-5 py-2 rounded-full text-sm bg-neutral-950 dark:bg-white dark:text-neutral-950 text-white font-semibold" data-testid="admin-editor-save">Save</button>
        </div>
      </div>
    </div>
  );
}

const Label = ({ children }) => <div className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground mb-1">{children}</div>;
const F = ({ label, value, onChange, type = "text", required, testId }) => (
  <div><Label>{label}</Label><input type={type} required={required} value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full h-10 rounded-lg border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold-400" data-testid={testId} /></div>
);
const TA = ({ label, value, onChange, rows = 3 }) => (
  <div><Label>{label}</Label><textarea rows={rows} value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold-400" /></div>
);

function PushTab() {
  const [stats, setStats] = useState(null);
  const [broadcasts, setBroadcasts] = useState([]);
  const [form, setForm] = useState({ title: "", body: "", url: "/", topic: "" });
  const [sending, setSending] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [s, b] = await Promise.all([
        api.get("/admin/push/subscribers", authHeader()),
        api.get("/admin/push/broadcasts", authHeader()),
      ]);
      setStats(s.data);
      setBroadcasts(b.data.items || []);
    } catch (e) {
      toast.error("Could not load push data");
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const send = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim()) {
      toast.error("Title and body are required");
      return;
    }
    setSending(true);
    try {
      const r = await api.post("/admin/push/broadcast", form, authHeader());
      toast.success(`Sent to ${r.data.sent} devices · ${r.data.failed} failed · ${r.data.removed} stale removed`);
      setForm({ title: "", body: "", url: "/", topic: "" });
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  return (
    <div className="space-y-6" data-testid="push-tab">
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <StatCard label="Total subscribers" value={stats?.total ?? "…"} />
        {stats?.by_topic && Object.entries(stats.by_topic).map(([k, v]) => (
          <StatCard key={k} label={k} value={v} />
        ))}
      </div>

      <form onSubmit={send} className="rounded-2xl border border-border bg-muted/20 p-6 space-y-3">
        <div className="flex items-center gap-2 text-sm font-semibold"><Send className="w-4 h-4 text-gold-500" /> New broadcast</div>
        <F label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} required testId="push-title" />
        <TA label="Message" value={form.body} onChange={(v) => setForm({ ...form, body: v })} rows={3} />
        <div className="grid sm:grid-cols-2 gap-3">
          <F label="Landing URL" value={form.url} onChange={(v) => setForm({ ...form, url: v })} testId="push-url" />
          <div>
            <Label>Topic (optional filter)</Label>
            <select value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })}
              className="w-full h-10 rounded-lg border border-border bg-background px-3 text-sm">
              <option value="">All subscribers</option>
              <option value="launches">Product launches</option>
              <option value="promotions">Promotions</option>
              <option value="distributor">Distributor announcements</option>
              <option value="news">Company news</option>
            </select>
          </div>
        </div>
        <button
          type="submit" disabled={sending}
          className="inline-flex items-center gap-2 bg-gold-400 hover:bg-gold-300 disabled:opacity-50 text-neutral-950 rounded-full px-5 py-2 text-sm font-bold uppercase tracking-widest"
          data-testid="push-send-btn"
        >{sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send broadcast</button>
      </form>

      <div>
        <div className="text-sm font-semibold mb-2 flex items-center gap-2"><Bell className="w-4 h-4 text-gold-500" /> Recent broadcasts</div>
        {broadcasts.length === 0 ? (
          <div className="text-sm text-muted-foreground p-6 rounded-xl border border-border bg-muted/10">No broadcasts sent yet.</div>
        ) : (
          <ul className="divide-y divide-border rounded-2xl border border-border overflow-hidden">
            {broadcasts.map((b) => (
              <li key={b.id} className="p-4 hover:bg-muted/30 text-sm">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="font-semibold">{b.title}</div>
                    <div className="text-muted-foreground text-xs mt-1">{b.body}</div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-2">
                      {new Date(b.created_at).toLocaleString()} · {b.topic || "all"} · sent {b.result?.sent} / failed {b.result?.failed}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="p-4 rounded-xl bg-background border border-border">
      <div className="font-display font-black text-2xl gold-gradient-text">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">{label.replace(/_/g, " ")}</div>
    </div>
  );
}

